import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Spinner } from 'components/ui/spinner'
import { formatCurrency } from 'features/Financials/utils/formatting'
import type { PortfolioFinancialsResponse } from 'types/portfolioFinancials'

export interface ConsolidatedPortfolioFinancialsGridProps {
	data: PortfolioFinancialsResponse
	isLoading?: boolean
}

/**
 * Read-only consolidated view of a portfolio's financials.
 * Rows = locations (with avatar + name in the first sticky column).
 * Columns = unioned line items (defaults first by order, then customs).
 * Calculated rows (gross_profit, earnings, etc.) are visually distinguished
 * with a subtle background and italic value text.
 *
 * Used by the PortfolioFinancials page (MYD-255). Backed by the
 * portfolioFinancialsSlice (MYD-253).
 */
export const ConsolidatedPortfolioFinancialsGrid = ({
	data,
	isLoading,
}: ConsolidatedPortfolioFinancialsGridProps) => {
	if (isLoading) {
		return <Spinner containerHeight="200px" type="circular" />
	}

	if (!data?.locations?.length) {
		return (
			<div className="text-center py-12 text-sm text-gray-500 italic">
				No locations in this portfolio.
			</div>
		)
	}

	const sortedLineItems = [...data.lineItems].sort((a, b) => a.order - b.order)

	// Column totals across all locations. For linear formulas (sales - cogs etc.)
	// summing the calculated column equals re-evaluating the formula against the
	// summed inputs, so a simple per-column sum is correct for both kinds.
	const columnTotals: Record<string, number> = {}
	for (const item of sortedLineItems) {
		let total = 0
		for (const loc of data.locations) {
			total += loc.lineItemValues?.[item.id] ?? 0
		}
		columnTotals[item.id] = total
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
			<table className="w-full border-collapse" role="grid">
				<thead>
					<tr className="bg-gray-100">
						<th
							scope="col"
							className="px-3 py-2 font-semibold text-gray-700 border-r border-b border-gray-300 sticky left-0 bg-gray-100 z-10"
							style={{ minWidth: 220 }}
						>
							<div className="flex items-center justify-between gap-2">
								<span>Location</span>
								<span className="text-xs font-normal text-gray-500">Most recent 4 Qtrs</span>
							</div>
						</th>
						{sortedLineItems.map(item => (
							<th
								key={item.id}
								scope="col"
								className={cn(
									'px-3 py-2 text-right font-semibold text-gray-700 border-r border-b border-gray-300 whitespace-nowrap',
									item.type === 'calculated' && 'bg-gray-50',
								)}
								style={{ minWidth: 120 }}
								title={item.formula ? `Formula: ${item.formula}` : undefined}
							>
								{item.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.locations.map(loc => (
						<tr key={loc.locationId} className="group">
							<th
								scope="row"
								className="px-3 py-2 text-left font-medium text-gray-900 border-r border-b border-gray-200 sticky left-0 bg-white z-10 group-hover:bg-gray-50"
							>
								<div className="flex items-center gap-2 min-w-0">
									<Avatar className="h-8 w-8 flex-shrink-0">
										<AvatarImage src={loc.image} alt={loc.name} />
										<AvatarFallback className="text-xs">
											{loc.name?.charAt(0)?.toUpperCase() || 'L'}
										</AvatarFallback>
									</Avatar>
									<span className="truncate">{loc.name || 'Untitled'}</span>
								</div>
							</th>
							{sortedLineItems.map(item => {
								const value = loc.lineItemValues?.[item.id] ?? 0
								return (
									<td
										key={item.id}
										className={cn(
											'px-3 py-2 text-right border-r border-b border-gray-200 tabular-nums whitespace-nowrap group-hover:bg-gray-50',
											item.type === 'calculated' && 'bg-gray-50 italic text-gray-700',
										)}
									>
										{formatCurrency(value)}
									</td>
								)
							})}
						</tr>
					))}
				</tbody>
				<tfoot>
					<tr className="bg-gray-50">
						<th
							scope="row"
							className="px-3 py-2 text-left font-bold text-gray-900 border-r border-t-2 border-gray-300 sticky left-0 bg-gray-50 z-10"
						>
							Totals
						</th>
						{sortedLineItems.map(item => (
							<td
								key={item.id}
								className={cn(
									'px-3 py-2 text-right font-bold border-r border-t-2 border-gray-300 tabular-nums whitespace-nowrap',
									item.type === 'calculated' && 'bg-gray-100 italic text-gray-800',
								)}
							>
								{formatCurrency(columnTotals[item.id] ?? 0)}
							</td>
						))}
					</tr>
				</tfoot>
			</table>
		</div>
	)
}

export default ConsolidatedPortfolioFinancialsGrid
